import { apiRequest } from '../../../shared/api';
import {
  Capa,
  CapaDashboardStats,
  CapaStatus,
  CapaType,
  CapaPriority,
  CapaTriggerSource,
  CapaFindingLink,
  CapaActionLink,
  CapaCorrection,
  CapaRootCause,
  CapaVerification,
  CapaEffectivenessCheck,
  CapaExtension,
} from './types';

export const capaService = {
  getDashboardStats: (organizationId: string = 'ORG-XYZ') =>
    apiRequest<CapaDashboardStats>(`/capas/analytics/dashboard?organizationId=${organizationId}`),

  getCapas: (params: {
    organizationId?: string;
    status?: CapaStatus;
    capaType?: CapaType;
    priority?: CapaPriority;
    department?: string;
    triggerSource?: CapaTriggerSource;
  } = {}) => {
    const query = new URLSearchParams();
    query.append('organizationId', params.organizationId || 'ORG-XYZ');
    if (params.status) query.append('status', params.status);
    if (params.capaType) query.append('capaType', params.capaType);
    if (params.priority) query.append('priority', params.priority);
    if (params.department) query.append('department', params.department);
    if (params.triggerSource) query.append('triggerSource', params.triggerSource);
    return apiRequest<Capa[]>(`/capas?${query.toString()}`);
  },

  getCapaById: (id: string) => apiRequest<Capa>(`/capas/${id}`),

  createCapa: (data: {
    organizationId?: string;
    title: string;
    problemStatement: string;
    description?: string;
    capaType?: CapaType;
    category?: string;
    priority?: CapaPriority;
    severity?: string;
    triggerSource?: CapaTriggerSource;
    triggerReference?: string;
    department: string;
    location?: string;
    ownerId: string;
    ownerName: string;
    coordinatorId?: string;
    coordinatorName?: string;
    dueDate: string;
    authorityDueDate?: string;
    requiresRootCause?: boolean;
    requiresApproval?: boolean;
    requiresVerification?: boolean;
    requiresEffectiveness?: boolean;
    isSystemic?: boolean;
    estimatedCost?: number;
    createdBy?: string;
  }) => apiRequest<Capa>('/capas', { method: 'POST', body: JSON.stringify(data) }),

  transitionStatus: (id: string, toStatus: CapaStatus, reason?: string, actor?: string) =>
    apiRequest<Capa>(`/capas/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ toStatus, reason, actor }),
    }),

  approvePlan: (id: string, approverId: string, approverName: string, notes?: string) =>
    apiRequest<Capa>(`/capas/${id}/approve-plan`, {
      method: 'POST',
      body: JSON.stringify({ approverId, approverName, notes }),
    }),

  linkFinding: (id: string, data: { findingId: string; findingNumber: string; title: string; linkedBy?: string }) =>
    apiRequest<CapaFindingLink>(`/capas/${id}/finding-links`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  linkAction: (id: string, data: {
    actionId: string;
    actionType: 'CORRECTIVE' | 'PREVENTIVE';
    title: string;
    ownerId: string;
    status?: string;
  }) =>
    apiRequest<CapaActionLink>(`/capas/${id}/action-links`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  addCorrection: (id: string, data: {
    description: string;
    containmentAction?: string;
    takenBy?: string;
    status?: string;
  }) =>
    apiRequest<CapaCorrection>(`/capas/${id}/corrections`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  addRootCause: (id: string, data: {
    method: 'FIVE_WHY' | 'FISHBONE' | 'BARRIER_ANALYSIS' | 'HFACS' | 'BOW_TIE' | 'CUSTOM';
    rootCauseType: string;
    description: string;
    verifiedBy?: string;
  }) =>
    apiRequest<CapaRootCause>(`/capas/${id}/root-causes`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  addVerification: (id: string, data: {
    verifierId: string;
    verifierName: string;
    result: 'EFFECTIVE' | 'PARTIALLY_EFFECTIVE' | 'INEFFECTIVE';
    notes: string;
  }) =>
    apiRequest<CapaVerification>(`/capas/${id}/verifications`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  addEffectivenessCheck: (id: string, data: {
    metricName: string;
    criteria: string;
    baselineValue?: string;
    targetValue?: string;
    actualValue?: string;
    evaluationDate: string;
    evaluatorId: string;
    evaluatorName: string;
    result: 'EFFECTIVE' | 'PARTIALLY_EFFECTIVE' | 'INEFFECTIVE';
    notes?: string;
  }) =>
    apiRequest<CapaEffectivenessCheck>(`/capas/${id}/effectiveness`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  requestExtension: (id: string, data: {
    newDueDate: string;
    reason: string;
    riskAssessment?: string;
    requesterId: string;
    requesterName: string;
  }) =>
    apiRequest<CapaExtension>(`/capas/${id}/extensions`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  approveExtension: (extensionId: string, approverId?: string, approverName?: string) =>
    apiRequest<CapaExtension>(`/capas/extensions/${extensionId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approverId, approverName }),
    }),

  closeCapa: (id: string, closedBy?: string, comment?: string) =>
    apiRequest<Capa>(`/capas/${id}/close`, {
      method: 'POST',
      body: JSON.stringify({ closedBy, comment }),
    }),

  reopenCapa: (id: string, reopenedBy?: string, reason?: string) =>
    apiRequest<Capa>(`/capas/${id}/reopen`, {
      method: 'POST',
      body: JSON.stringify({ reopenedBy, reason }),
    }),
};
